/**
 * CemeteriesDirectory — cross-border cemetery listing.
 *
 * Public read-only route at `/cemeteries` powering the "Cemeteries · A
 * Cross-border Directory" wing on MyFamilyTree.care and the linked
 * cemetery reference on the funeral-traditions article on IFT.
 *
 * Server: `GET /api/cemeteries` + `/api/cemeteries/meta/facets`.
 */
import { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { http } from "@/lib/api";
import SeoHead from "@/components/SeoHead";
import { MapPin, Search, Church, Landmark, Flame, Cross, Moon, Sparkles, Star, ShieldAlert } from "lucide-react";

// Religion → visual bucket. Icon + accent colour used on filter chips and
// inside individual cemetery cards.
const RELIGION_ICON = {
  christian: { icon: Cross,       label: "Christian",  color: "#0369A1" },
  muslim:    { icon: Moon,        label: "Muslim",     color: "#059669" },
  hindu:     { icon: Flame,       label: "Hindu",      color: "#B25D38" },
  sikh:      { icon: Sparkles,    label: "Sikh",       color: "#EA580C" },
  parsi:     { icon: Star,        label: "Parsi",      color: "#7C3AED" },
  jewish:    { icon: Star,        label: "Jewish",     color: "#B45309" },
  military:  { icon: ShieldAlert, label: "Military",   color: "#334155" },
  mixed:     { icon: Landmark,    label: "Multi-faith",color: "#475569" },
  municipal: { icon: Landmark,    label: "Municipal",  color: "#475569" },
};
const relBucket = (r) => RELIGION_ICON[(r || "mixed").toLowerCase()] || RELIGION_ICON.mixed;

export default function CemeteriesDirectory() {
  const [items, setItems]         = useState([]);
  const [total, setTotal]         = useState(0);
  const [loading, setLoading]     = useState(true);
  const [q, setQ]                 = useState("");
  const [religion, setReligion]   = useState("");
  const [country, setCountry]     = useState("");
  const [facets, setFacets]       = useState({ geo: [], religions: [], total: 0 });

  // Load facets once so the filters know what's available in the DB.
  useEffect(() => {
    let alive = true;
    http.get("/cemeteries/meta/facets")
      .then(({ data }) => alive && setFacets(data))
      .catch(() => {});
    return () => { alive = false; };
  }, []);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    const params = new URLSearchParams();
    if (q.trim())        params.set("q", q.trim());
    if (religion)        params.set("religion", religion);
    if (country)         params.set("country", country);
    params.set("limit", "60");
    http.get(`/cemeteries?${params}`)
      .then(({ data }) => {
        if (!alive) return;
        setItems(data.items || []);
        setTotal(data.total || 0);
      })
      .catch(() => alive && setItems([]))
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [q, religion, country]);

  // Distinct countries sorted by cemetery count — powers the country
  // filter chips. Uniqueness is aggregated client-side because the
  // facets endpoint returns geo pairs (country + state).
  const countries = useMemo(() => {
    const map = new Map();
    for (const g of facets.geo || []) {
      map.set(g.country, (map.get(g.country) || 0) + g.count);
    }
    return Array.from(map.entries())
      .sort((a, b) => b[1] - a[1])
      .map(([name, n]) => ({ name, n }));
  }, [facets]);

  return (
    <div className="max-w-6xl mx-auto px-4 lg:px-8 py-8" data-testid="cemeteries-directory">
      <SeoHead
        title="Cemeteries Directory · India + Diaspora"
        description="A cross-border directory of Christian, Muslim, Hindu, Parsi, Jewish and military cemeteries and cremation grounds across India and the Indian diaspora."
      />

      <div className="overline mb-2" style={{ color: "var(--wofy-primary)" }}>
        § A CROSS-BORDER DIRECTORY
      </div>
      <h1 className="font-display text-4xl md:text-5xl leading-tight">Cemeteries &amp; Burial Grounds</h1>
      <p className="mt-3 text-lg max-w-3xl" style={{ color: "var(--wofy-text-soft)" }}>
        {total.toLocaleString()} sites across {countries.length} countries — historic churchyards, kabristans,
        cremation ghats, towers of silence and military war graves that hold India's families and their diaspora.
      </p>
      <p className="mt-2 text-sm">
        <Link
          to="/articles/indian-funeral-traditions-and-memorial-practices"
          className="underline"
          style={{ color: "var(--wofy-primary)" }}
          data-testid="cemeteries-funeral-link"
        >
          Read: Indian Funeral Traditions &amp; Memorial Practices →
        </Link>
      </p>

      {/* Filters */}
      <div className="mt-6 wofy-card p-4" style={{ borderColor: "var(--wofy-border)" }}>
        <div className="flex flex-col md:flex-row gap-3 items-stretch md:items-center">
          <div className="relative flex-1">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 opacity-60" />
            <input
              type="text"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name or city…"
              className="w-full pl-9 pr-3 py-2 rounded-lg border text-sm bg-transparent"
              style={{ borderColor: "var(--wofy-border)" }}
              data-testid="cemeteries-search"
            />
          </div>
          <select
            value={country}
            onChange={(e) => setCountry(e.target.value)}
            className="px-3 py-2 rounded-lg border text-sm bg-transparent"
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="cemeteries-filter-country"
          >
            <option value="">All countries</option>
            {countries.map((c) => (
              <option key={c.name} value={c.name}>{c.name} ({c.n})</option>
            ))}
          </select>
        </div>
        {/* Religion chips */}
        <div className="mt-3 flex flex-wrap gap-1.5" data-testid="cemeteries-religion-chips">
          <button
            type="button"
            onClick={() => setReligion("")}
            className={`rounded-full border px-3 py-1 text-xs ${religion === "" ? "bg-slate-900 text-white" : ""}`}
            style={{ borderColor: "var(--wofy-border)" }}
            data-testid="cemeteries-religion-all"
          >
            All ({facets.total})
          </button>
          {(facets.religions || []).map((r) => {
            const b = relBucket(r.religion);
            const active = religion === r.religion;
            const Icon = b.icon;
            return (
              <button
                key={r.religion}
                type="button"
                onClick={() => setReligion(active ? "" : r.religion)}
                className={`rounded-full border px-3 py-1 text-xs inline-flex items-center gap-1 ${active ? "text-white" : ""}`}
                style={{
                  borderColor: b.color,
                  color: active ? "#fff" : b.color,
                  background: active ? b.color : "transparent",
                }}
                data-testid={`cemeteries-religion-${r.religion}`}
              >
                <Icon className="w-3 h-3" /> {b.label} ({r.count})
              </button>
            );
          })}
        </div>
      </div>

      {/* Results */}
      <div className="mt-6" data-testid="cemeteries-results">
        {loading ? (
          <div className="text-sm opacity-70">Loading directory…</div>
        ) : items.length === 0 ? (
          <div className="text-sm opacity-70">No cemeteries match those filters.</div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {items.map((c) => {
              const b = relBucket(c.religion);
              const Icon = b.icon;
              return (
                <Link
                  key={c.slug}
                  to={`/cemeteries/${c.slug}`}
                  className="wofy-card p-4 hover:shadow-md transition"
                  style={{ borderColor: "var(--wofy-border)" }}
                  data-testid={`cemetery-card-${c.slug}`}
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0">
                      <div className="font-display text-lg leading-tight truncate">{c.name}</div>
                      {c.aka && <div className="text-xs opacity-70 truncate">{c.aka}</div>}
                    </div>
                    <span
                      className="shrink-0 inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[10px] font-mono uppercase"
                      style={{ background: `${b.color}18`, color: b.color }}
                    >
                      <Icon className="w-3 h-3" /> {b.label}
                    </span>
                  </div>
                  <div className="mt-2 text-xs flex items-center gap-1" style={{ color: "var(--wofy-text-soft)" }}>
                    <MapPin className="w-3 h-3" /> {[c.city, c.state, c.country].filter(Boolean).join(" · ")}
                  </div>
                  {c.summary && (
                    <p className="mt-2 text-xs leading-relaxed line-clamp-3">{c.summary}</p>
                  )}
                  <div className="mt-3 flex items-center gap-3 text-[11px] font-mono uppercase opacity-70">
                    {c.established && <span>Est. {c.established > 0 ? c.established : `${Math.abs(c.established)} BCE`}</span>}
                    {c.burial_count && <span>· {c.burial_count}</span>}
                  </div>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    </div>
  );
}
